// ============================================================
//  TAX REPORT PAGE — ADMIN DASHBOARD
//  Path: src/pages/TaxReportPage.tsx
//
//  Reads the permanent tax ledger (one row per booking per component).
//  Answers "how much tax did we charge on <component> in <province>
//  between <dates>, who bears it and who remits it". Refund rows are
//  negative, so every total is already net of refunds.
//
//  Access: super_admin, admin only.
// ============================================================

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getTaxLedgerReport, getTaxLedgerCsv, getTaxLedgerPdf } from '../services/admin.service'
import { TaxLedgerReport, TaxLedgerGroup } from '../types'

const PAGE_SIZE = 50

// Drops empty filters. Lives outside the component so it is not a hook dependency.
const clean = (f: Record<string, string>) => Object.fromEntries(Object.entries(f).filter(([, v]) => v.trim() !== ''))

const COMPONENT_LABELS: Record<string, string> = {
  JOB_PRICE: 'Job price',
  CUSTOMER_SERVICE_FEE: 'Service fee (customer)',
  CUSTOMER_INSURANCE: 'Insurance (customer)',
  TASKER_COMMISSION: 'Commission (tasker)',
  TASKER_SERVICE_FEE: 'Service fee (tasker)',
  TASKER_INSURANCE: 'Insurance (tasker)',
  PROCESSING_FEE: 'Processing fee',
}
const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT']

const money = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const errText = (err: any, fallback: string) =>
  err?.response?.data?.message || (err?.isNetworkError
    ? 'Could not reach the server — check your connection and try again.'
    : fallback)

const GroupTable: React.FC<{ title: string; groups: TaxLedgerGroup[]; label?: (k: string) => string }> = ({ title, groups, label }) => (
  <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
    <div className="px-5 py-3 border-b border-border-light"><h2 className="text-sm font-semibold text-white">{title}</h2></div>
    {groups.length === 0 ? (
      <p className="text-center py-6 text-gray-400 text-sm">No entries</p>
    ) : (
      <table className="w-full">
        <thead>
          <tr className="border-b border-border-light">
            <th className="text-left px-5 py-2 text-xs font-semibold text-gray-400 uppercase">&nbsp;</th>
            <th className="text-right px-5 py-2 text-xs font-semibold text-gray-400 uppercase">Base</th>
            <th className="text-right px-5 py-2 text-xs font-semibold text-gray-400 uppercase">Tax</th>
            <th className="text-right px-5 py-2 text-xs font-semibold text-gray-400 uppercase">Rows</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.key} className="border-b border-border-light last:border-0">
              <td className="px-5 py-2 text-sm text-gray-200">{label ? label(g.key) : g.key}</td>
              <td className="px-5 py-2 text-sm text-gray-300 text-right">{money(g.baseAmount)}</td>
              <td className="px-5 py-2 text-sm text-white text-right font-medium">{money(g.taxAmount)}</td>
              <td className="px-5 py-2 text-sm text-gray-400 text-right">{g.entries}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
)

const inputCls = 'bg-surface-tertiary border border-border-light rounded-xl px-3 py-2 text-sm text-white'

const TaxReportPage: React.FC = () => {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [province, setProvince] = useState('')
  const [component, setComponent] = useState('')
  const [entryType, setEntryType] = useState('')
  const [expertId, setExpertId] = useState('')
  const [taskId, setTaskId] = useState('')
  const [page, setPage] = useState(1)
  // Filters apply on "Apply", not on every keystroke.
  const [applied, setApplied] = useState({ from: '', to: '', province: '', component: '', entryType: '', expertId: '', taskId: '' })

  const [report, setReport] = useState<TaxLedgerReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [csvBusy, setCsvBusy] = useState(false)
  const [csvError, setCsvError] = useState('')
  const [pdfBusy, setPdfBusy] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let superseded = false
    const load = async () => {
      setLoading(true)
      setLoadError('')
      try {
        const res = await getTaxLedgerReport({ ...clean(applied), page, limit: PAGE_SIZE })
        if (!superseded) setReport(res.data.data)
      } catch (err: any) {
        if (superseded) return
        console.error('Failed to load tax report:', err)
        setReport(null)
        setLoadError(errText(err, 'Failed to load the tax report.'))
      } finally {
        if (!superseded) setLoading(false)
      }
    }
    load()
    return () => { superseded = true }
  }, [applied, page, reloadKey])

  const apply = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    setApplied({ from, to, province, component, entryType, expertId, taskId })
  }

  const reset = () => {
    setFrom(''); setTo(''); setProvince(''); setComponent(''); setEntryType(''); setExpertId(''); setTaskId('')
    setPage(1)
    setApplied({ from: '', to: '', province: '', component: '', entryType: '', expertId: '', taskId: '' })
  }

  // The CSV is requested as a blob, so an error body arrives as a Blob too.
  const downloadCsv = async () => {
    setCsvBusy(true)
    setCsvError('')
    try {
      const res = await getTaxLedgerCsv(clean(applied))
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `tax-report-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
      if (res.headers?.['x-report-truncated'] === 'true') {
        setCsvError('The file was cut off at 50,000 rows. Narrow the date range and download again to get the rest.')
      }
    } catch (err: any) {
      let message = errText(err, 'Could not download the CSV.')
      try {
        if (err?.response?.data instanceof Blob) message = JSON.parse(await err.response.data.text()).message || message
      } catch { /* keep the generic message */ }
      setCsvError(message)
    } finally {
      setCsvBusy(false)
    }
  }

  // Same filters as the screen; the PDF holds the newest 1,000 rows plus all totals.
  const downloadPdf = async () => {
    setPdfBusy(true)
    setCsvError('')
    try {
      const res = await getTaxLedgerPdf(clean(applied))
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `tax-report-${new Date().toISOString().slice(0, 10)}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    } catch (err: any) {
      let message = errText(err, 'Could not download the PDF.')
      try {
        if (err?.response?.data instanceof Blob) message = JSON.parse(await err.response.data.text()).message || message
      } catch { /* keep the generic message */ }
      setCsvError(message)
    } finally {
      setPdfBusy(false)
    }
  }

  const totalRows = report?.totals.entries ?? 0
  const lastPage = Math.max(1, Math.ceil(totalRows / PAGE_SIZE))

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Tax Report</h1>
          <p className="text-gray-400 text-sm mt-1">
            Permanent record of tax on every booking, by component, province and who remits it. Refunds are subtracted.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Link to="/tax-rates" className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Province rates</Link>
          <button onClick={downloadCsv} disabled={csvBusy}
            className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors">
            {csvBusy ? 'Preparing…' : '⬇ Download CSV'}
          </button>
          <button onClick={downloadPdf} disabled={pdfBusy}
            className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors">
            {pdfBusy ? 'Preparing…' : '⬇ Download PDF'}
          </button>
        </div>
      </div>

      <form onSubmit={apply} className="bg-surface-card border border-border-light rounded-2xl p-4 mb-6 flex flex-wrap gap-3 items-end">
        <label className="text-xs text-gray-400">From<br />
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} /></label>
        <label className="text-xs text-gray-400">To<br />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} /></label>
        <label className="text-xs text-gray-400">Province<br />
          <select value={province} onChange={(e) => setProvince(e.target.value)} className={inputCls}>
            <option value="">All</option>
            {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select></label>
        <label className="text-xs text-gray-400">Component<br />
          <select value={component} onChange={(e) => setComponent(e.target.value)} className={inputCls}>
            <option value="">All</option>
            {Object.entries(COMPONENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></label>
        <label className="text-xs text-gray-400">Entry type<br />
          <select value={entryType} onChange={(e) => setEntryType(e.target.value)} className={inputCls}>
            <option value="">All</option>
            <option value="HIRE">Booking</option>
            <option value="ADJUSTMENT">Adjustment</option>
            <option value="REFUND">Refund</option>
          </select></label>
        <label className="text-xs text-gray-400">Tasker ID<br />
          <input value={expertId} onChange={(e) => setExpertId(e.target.value)} className={inputCls} placeholder="optional" /></label>
        <label className="text-xs text-gray-400">Task ID<br />
          <input value={taskId} onChange={(e) => setTaskId(e.target.value)} className={inputCls} placeholder="optional" /></label>
        <button type="submit" className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 transition-colors">Apply</button>
        <button type="button" onClick={reset} className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Reset</button>
      </form>

      {csvError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {csvError}
          <button onClick={() => setCsvError('')} className="ml-3 text-red-300 hover:text-white">✕</button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : loadError ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{loadError}</p>
          <button onClick={() => setReloadKey((k) => k + 1)}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">Try again</button>
        </div>
      ) : !report ? (
        <div className="text-center py-8 text-gray-400">No data available</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-surface-card border border-border-light rounded-2xl p-5">
              <p className="text-2xl font-bold text-white">{money(report.totals.taxAmount)}</p>
              <p className="text-sm text-gray-400 mt-1">Total tax</p>
            </div>
            <div className="bg-surface-card border border-border-light rounded-2xl p-5">
              <p className="text-2xl font-bold text-white">{money(report.totals.baseAmount)}</p>
              <p className="text-sm text-gray-400 mt-1">Amount tax was worked out on</p>
            </div>
            <div className="bg-surface-card border border-border-light rounded-2xl p-5">
              <p className="text-2xl font-bold text-white">{report.totals.entries}</p>
              <p className="text-sm text-gray-400 mt-1">Ledger rows</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <GroupTable title="By province" groups={report.byProvince} />
            <GroupTable title="By component" groups={report.byComponent} label={(k) => COMPONENT_LABELS[k] || k} />
            <GroupTable title="Who remits it" groups={report.byRemitter}
              label={(k) => (k === 'PLATFORM' ? 'MatchnBook (platform fees)' : k === 'TASKER' ? 'Tasker (job price tax)' : k)} />
          </div>

          <div className="bg-surface-card border border-border-light rounded-2xl overflow-x-auto">
            <div className="px-5 py-4 border-b border-border-light"><h2 className="text-lg font-semibold text-white">Entries</h2></div>
            {report.rows.length === 0 ? (
              <p className="text-center py-8 text-gray-400">No entries match these filters</p>
            ) : (
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="border-b border-border-light">
                    {['Date', 'Task', 'Type', 'Component', 'Place', 'Base', 'Rate', 'Tax', 'Borne by', 'Remitted by'].map((h) => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.id} className="border-b border-border-light last:border-0">
                      <td className="px-4 py-2 text-xs text-gray-400 whitespace-nowrap">{new Date(r.createdAt).toLocaleString()}</td>
                      <td className="px-4 py-2 text-xs"><span title={r.taskId} className="text-gray-200 font-mono">{r.taskId.slice(0, 8)}…</span></td>
                      <td className="px-4 py-2 text-xs text-gray-300">{r.entryType === 'HIRE' ? 'Booking' : r.entryType === 'REFUND' ? 'Refund' : 'Adjustment'}</td>
                      <td className="px-4 py-2 text-xs text-gray-200">{COMPONENT_LABELS[r.component] || r.component}</td>
                      <td className="px-4 py-2 text-xs text-gray-300">{r.country}{r.province ? ` / ${r.province}` : ''}{r.jurisdictionLabel ? ` · ${r.jurisdictionLabel}` : ''}</td>
                      <td className="px-4 py-2 text-xs text-gray-300">{money(r.baseAmount)}</td>
                      <td className="px-4 py-2 text-xs text-gray-300">{r.taxable ? `${r.taxRatePercent}%` : 'not taxed'}</td>
                      <td className={`px-4 py-2 text-xs font-medium ${r.taxAmount < 0 ? 'text-red-400' : 'text-white'}`}>{money(r.taxAmount)}</td>
                      <td className="px-4 py-2 text-xs text-gray-300">{r.taxPayer === 'CUSTOMER' ? 'Customer' : 'Tasker'}</td>
                      <td className="px-4 py-2 text-xs text-gray-300">{r.taxRemittedBy === 'PLATFORM' ? 'MatchnBook' : 'Tasker'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {totalRows > PAGE_SIZE && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-border-light text-sm text-gray-400">
                <span>Page {page} of {lastPage}</span>
                <div className="flex gap-2">
                  <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1.5 bg-surface-tertiary rounded-lg disabled:opacity-40">Previous</button>
                  <button disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 bg-surface-tertiary rounded-lg disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default TaxReportPage